import DOMPurify from "dompurify";

type SanitizeConfig = Parameters<typeof DOMPurify.sanitize>[1];

const DEFAULT_CONFIG: SanitizeConfig = {
  USE_PROFILES: { html: true }
};

export const sanitizeData = (html: string, config?: SanitizeConfig): string => {
  return DOMPurify.sanitize(html, { ...DEFAULT_CONFIG, ...config }) as string;
};

/**
 * Оставляет только буквы (a-z, A-Z, а-я, А-Я) и цифры (0-9), удаляет все остальные символы, включая пробелы и специальные знаки.
 * Для строк возвращает string, для чисел возвращает number, если результат — число, иначе string.
 * @param input Входные данные: строка или число
 * @returns Очищенное значение (string для строк, number или string для чисел)
 */
export const cleanText = <T extends string | number>(
  input: T
): T extends string ? string : number | string => {
  if (!input && input !== 0) {
    return "" as any; // Приводим к любому типу для совместимости
  }

  const isNumberInput = typeof input === "number";
  const strInput = String(input);
  // Разрешаем латинские буквы (a-z, A-Z), русские буквы (а-я, А-Я) и цифры (0-9)
  const cleaned = strInput.replace(/[^a-zA-Zа-яА-Я0-9]/g, "");

  if (isNumberInput) {
    const numResult = Number(cleaned);
    return (isNaN(numResult) ? cleaned : numResult) as T extends string ? string : number | string;
  }

  return cleaned as T extends string ? string : number | string;
};

// Функция рекурсивной очистки данных в объекте
export const sanitizeObject = (data: any): any => {
  if (typeof data === "string") {
    return sanitizeData(data.trim());
  } else if (Array.isArray(data)) {
    return data.map(item => sanitizeObject(item));
  } else if (data && typeof data === "object") {
    return Object.keys(data).reduce(
      (acc, key) => {
        acc[key] = sanitizeObject(data[key]);
        return acc;
      },
      {} as Record<string, any>
    );
  }
  return data;
};

/**
 * Очищает и нормализует URL, защищая от XSS и некорректных данных.
 * Разрешает только протоколы https.
 * @param input Входной URL (строка)
 * @returns Безопасный URL или пустая строка, если вход некорректен
 */
export const sanitizeURL = (input: string): string => {
  if (!input || typeof input !== "string") {
    return "";
  }

  const cleanedInput = sanitizeData(input.trim());

  if (!cleanedInput) {
    return "";
  }

  const validUrlPattern = /^[A-Za-z0-9-._~:/?#[\]@!$&'()*+,;=]*$/;
  if (!validUrlPattern.test(cleanedInput)) {
    console.log("URL содержит недопустимые символы");
    return "";
  }

  // Проверяем, содержит ли строка протокол, отличный от https://
  if (/^[a-zA-Z]+:\/\//.test(cleanedInput) && !cleanedInput.startsWith("https://")) {
    console.log("URL содержит недопустимый протокол");
    return "";
  }

  try {
    const urlString = cleanedInput.match(/^https?:\/\//) ? cleanedInput : `https://${cleanedInput}`;
    const url = new URL(urlString);

    if (!["https:"].includes(url.protocol)) {
      return "";
    }

    let result = url.toString();

    if (result.endsWith("/")) {
      result = result.slice(0, -1);
    }
    return result;
  } catch {
    console.log(`Невалидный URL`);
    return "";
  }
};
